// WhitelabelRequestAccess — the SIGNED-IN non-member gate for a customer
// host (Nika 2026-08-30, option A: "a branded Request-an-invitation
// screen — no stranger enters the workspace").
//
// A visitor who signs in on app.<customer> with an account that is NOT a
// member of the host org used to land on the plain Symbols home (after
// NoOrgOverlay's whitelabel suppression removed the create-workspace ask).
// This full-viewport surface replaces that dead end with the SAME
// invitation contract the logged-out signup pane carries: the org's own
// published role list, one request, "we review every request".
//
// ── What it reuses, deliberately ─────────────────────────────────────────
// · scheme/accent/ground/mark — imported from WhitelabelSignin.js
//   (schemeOf/accentOf/whitelabelGround/sizedSvg), never re-derived: two
//   copies of the scheme precedence drift (MainLoader.js's argument).
// · WhitelabelRoleRow — the shared row component; its contract reads
//   `s.parent.suRole` and writes `s.parent.update({ suRole })`, so this
//   component's state key MUST stay `suRole`.
// · fetchWhitelabelOnboarding / submitWhitelabelOnboarding — the same
//   context functions the logged-out pane calls; the server matches the
//   role against the org's OWN allowlist and mints the invitation there.
//   The signed-in user's email is submitted VERBATIM (read-only in the
//   UI) — the invitation must land on the account that asked.
//
// ── Gating (fail closed on every unknown) ────────────────────────────────
// Shows ONLY when ALL of: auth ready + signed in; the host resolved a
// brand (root.whitelabelBrand); the host scope pin holds an org id
// (hostScopePin.js — null means "no opinion", so no gate); root.orgs is a
// LOADED array; and the pinned org is not among the user's orgs. Members,
// platform hosts, logged-out visitors and half-booted states all hide it.
// The mount site in app.js additionally gates on isCustomDomainHost(), so
// platform hosts never even mount the element.
import {
  whitelabelGround,
  schemeOf,
  accentOf,
  sizedSvg
} from '@symbo-ls/workspace-shared/components/WhitelabelSignin.js'
import { isWhitelabelNonMember } from '../../functions/whitelabelGate.js'

const _brandOf = (s) => (s && s.root && s.root.whitelabelBrand) || null

const _userOf = (root) => (root && root.user) || null

// The ONE predicate lives in functions/whitelabelGate.js (CreditBlocker
// and future stand-down surfaces share it); this alias keeps the
// component's test seam stable.
export const _isNonMember = isWhitelabelNonMember

// Fetch the org's published role list at most once per mount, and only
// while the gate is actually visible — a member must not cost the extra
// round-trip. Guarded through state so a remount re-reads (same shape as
// kickOnboarding in WhitelabelSignin.js).
const _kickRoles = (el, s) => {
  if (!_isNonMember(s.root)) return
  const b = _brandOf(s)
  if (!b || !b.workspaceKey || s.onboardingKicked) return
  s.update({ onboardingKicked: true }, { preventUpdate: true })
  Promise.resolve(el.call('fetchWhitelabelOnboarding', { workspaceKey: b.workspaceKey }))
    .then((cfg) => {
      // `captcha` rides on the SAME response as the roles and is present
      // only when this org requires one — so it is parked, never guessed,
      // and a later config change needs no redeploy.
      const captcha = (cfg && cfg.captcha) || null
      if (cfg && cfg.roles && cfg.roles.length)
        s.update({ onboardingRoles: cfg.roles, onboardingCaptcha: captcha })
      else s.update({ onboardingRoles: [], onboardingCaptcha: null })
    })
    .catch(() => s.update({ onboardingRoles: [], onboardingCaptcha: null }))
}

// Prefill the name from the signed-in account exactly once — typing must
// never be overwritten by a later auth refresh.
const _seedName = (el, s) => {
  if (s.wraNameSeeded) return
  const u = _userOf(s.root)
  if (!u) return
  s.update(
    { wraNameSeeded: true, wraName: (u.name && String(u.name)) || '' },
    { preventUpdate: true }
  )
}

export const WhitelabelRequestAccess = {
  tag: 'section',
  position: 'fixed',
  inset: '0',
  zIndex: 1200,
  overflowY: 'auto',
  flow: 'y',
  align: 'center center',
  padding: 'C A',
  hide: (el, s) => !_isNonMember(s.root),
  display: (el, s) => (_isNonMember(s.root) ? 'flex' : 'none'),
  background: (el, s) => whitelabelGround(s),
  color: (el, s) => schemeOf(s).ink,
  // Same reactive contract as NoOrgOverlay: the pieces of the predicate
  // land at different moments (auth snapshot, orgs, the late-applied
  // brand), and each write must re-evaluate the gate.
  subscribeTo: ['orgs', 'signedIn', '_authReady', 'whitelabelBrand', 'user', '_hostScopeOrgId'],

  state: {
    // WhitelabelRoleRow's binding contract — do not rename.
    suRole: '',
    onboardingKicked: false,
    onboardingRoles: null,
    onboardingCaptcha: null,
    // Written by WhitelabelTurnstile, read by Submit. Single-use: cleared
    // after every attempt.
    captchaToken: '',
    captchaWidgetId: null,
    wraName: '',
    wraNameSeeded: false,
    wraBusy: false,
    wraStatus: '',
    wraOk: false,
    wraSent: false
  },

  onRender: (el, s) => {
    _kickRoles(el, s)
    _seedName(el, s)
  },
  onUpdate: (el, s) => {
    _kickRoles(el, s)
    _seedName(el, s)
  },

  Lockup: {
    display: 'flex',
    flow: 'y',
    align: 'center center',
    gap: 'X',
    margin: '0 0 B',
    BrandGlyph: {
      tag: 'div',
      display: 'flex',
      align: 'center center',
      color: (el, s) => accentOf(s),
      html: (el, s) => {
        const b = _brandOf(s)
        return sizedSvg((b && b.logoSvg) || '', 44)
      },
      aria: { hidden: 'true' }
    },
    Wordmark: {
      tag: 'h1',
      fontSize: 'A2',
      fontWeight: '700',
      letterSpacing: '0.24em',
      textTransform: 'uppercase',
      lineHeight: '1',
      textAlign: 'center',
      margin: '0',
      color: (el, s) => accentOf(s),
      text: (el, s) => {
        const b = _brandOf(s)
        return (b && b.name) || ''
      }
    },
    Tagline: {
      tag: 'p',
      margin: '0',
      fontSize: 'Z',
      color: (el, s) => schemeOf(s).inkSoft,
      display: (el, s) => (_brandOf(s) && _brandOf(s).tagline ? 'block' : 'none'),
      text: (el, s) => (_brandOf(s) && _brandOf(s).tagline) || ''
    }
  },

  Card: {
    width: '100%',
    // The width the login card collapses to on one column — same surface
    // family, same measure.
    maxWidth: '440px',
    flow: 'y',
    gap: 'Y',
    padding: 'A B',
    round: 'radiusCard',
    background: (el, s) => schemeOf(s).card,
    boxShadow: (el, s) => schemeOf(s).hairline,

    Heading: {
      display: 'flex',
      align: 'center flex-start',
      gap: 'Y',
      AccentBar: {
        tag: 'span',
        width: '3px',
        height: '15px',
        round: 'radiusPill',
        background: (el, s) => accentOf(s)
      },
      H: {
        tag: 'h2',
        margin: '0',
        fontSize: 'Z3',
        fontWeight: '700',
        text: '{{ workspace.requestAccess.title | polyglot }}'
      }
    },

    // ── The request form (until the request is accepted by the server) ──
    Intro: {
      tag: 'p',
      margin: '0',
      fontSize: 'Z1',
      color: (el, s) => schemeOf(s).inkSoft,
      hide: (el, s) => !!s.wraSent,
      display: (el, s) => (s.wraSent ? 'none' : 'block'),
      // Two written-out sentences: with the brand's own name, and without
      // one. A translated "this workspace" in the `{workspace}` slot would
      // need a case form the Georgian sentence cannot give it.
      text: (el, s) => {
        const b = _brandOf(s)
        const u = _userOf(s.root)
        const who = (u && u.email) || el.call('polyglot', 'workspace.requestAccess.yourAccount')
        return b && b.name
          ? el
              .call('polyglot', 'workspace.requestAccess.intro')
              .replace('{who}', who)
              .replace('{workspace}', b.name)
          : el.call('polyglot', 'workspace.requestAccess.introNoName').replace('{who}', who)
      }
    },

    FormPane: {
      flow: 'y',
      gap: 'Y',
      hide: (el, s) => !!s.wraSent,
      display: (el, s) => (s.wraSent ? 'none' : 'flex'),

      RoleCaption: {
        tag: 'label',
        fontSize: 'Y',
        color: (el, s) => schemeOf(s).inkSoft,
        hide: (el, s) => !(s.onboardingRoles && s.onboardingRoles.length),
        display: (el, s) => (s.onboardingRoles && s.onboardingRoles.length ? 'block' : 'none'),
        text: '{{ workspace.requestAccess.question | polyglot }}'
      },
      RoleList: {
        display: 'flex',
        flow: 'y',
        gap: 'V',
        hide: (el, s) => !(s.onboardingRoles && s.onboardingRoles.length),
        childExtends: 'WhitelabelRoleRow',
        childrenAs: 'state',
        children: (el, s) => s.onboardingRoles || []
      },
      // The org published no self-serve roles — the request endpoint would
      // refuse, so say the honest thing instead of rendering a dead form.
      NoRolesNote: {
        tag: 'p',
        margin: '0',
        fontSize: 'Z',
        color: (el, s) => schemeOf(s).inkSoft,
        hide: (el, s) => s.onboardingRoles === null || s.onboardingRoles.length > 0,
        display: (el, s) =>
          s.onboardingRoles !== null && s.onboardingRoles.length === 0 ? 'block' : 'none',
        text: '{{ workspace.requestAccess.closed | polyglot }}'
      },

      NameCaption: {
        tag: 'label',
        fontSize: 'Y',
        color: (el, s) => schemeOf(s).inkSoft,
        hide: (el, s) => !(s.onboardingRoles && s.onboardingRoles.length),
        display: (el, s) => (s.onboardingRoles && s.onboardingRoles.length ? 'block' : 'none'),
        text: '{{ workspace.requestAccess.yourName | polyglot }}'
      },
      NameInput: {
        tag: 'input',
        type: 'text',
        autocomplete: 'name',
        width: '100%',
        padding: 'Y Z',
        round: 'radiusControl',
        background: (el, s) => schemeOf(s).field,
        color: (el, s) => schemeOf(s).ink,
        boxShadow: (el, s) => schemeOf(s).hairline,
        fontSize: 'Z1',
        borderWidth: '0',
        hide: (el, s) => !(s.onboardingRoles && s.onboardingRoles.length),
        display: (el, s) => (s.onboardingRoles && s.onboardingRoles.length ? 'block' : 'none'),
        value: (el, s) => s.wraName,
        onInput: (e, el, s) => s.update({ wraName: e.target.value }, { preventUpdate: true })
      },

      Status: {
        tag: 'div',
        fontSize: 'Y',
        minHeight: 'X',
        color: (el, s) => (s.wraOk ? schemeOf(s).ink : schemeOf(s).danger),
        text: (el, s) => s.wraStatus || ''
      },

      // The same shared slot the logged-out signup pane mounts, below the
      // status line for the same reason: the server's `captcha_failed`
      // message points "below". Absent captcha config → nothing renders.
      WhitelabelTurnstile: {},

      Submit: {
        tag: 'button',
        type: 'button',
        padding: 'Y A',
        round: 'radiusControl',
        borderWidth: '0',
        cursor: 'pointer',
        fontSize: 'Z1',
        fontWeight: '700',
        color: (el, s) => schemeOf(s).onAccent,
        background: (el, s) => accentOf(s),
        opacity: (el, s) => (s.wraBusy ? '0.6' : '1'),
        hide: (el, s) => !(s.onboardingRoles && s.onboardingRoles.length),
        display: (el, s) => (s.onboardingRoles && s.onboardingRoles.length ? 'block' : 'none'),
        text: (el, s) =>
          el.call(
            'polyglot',
            s.wraBusy ? 'workspace.requestAccess.sending' : 'workspace.requestAccess.submit'
          ),
        onClick: async (e, el, s) => {
          if (s.wraBusy) return
          const b = _brandOf(s)
          const u = _userOf(s.root)
          s.update({ wraBusy: true, wraStatus: '', wraOk: false })
          const res = await el.call('submitWhitelabelOnboarding', {
            workspaceKey: b && b.workspaceKey,
            name: s.wraName,
            email: (u && u.email) || '',
            role: s.suRole,
            captchaToken: s.captchaToken
          })
          // One token, one attempt — reset before a second press can replay
          // a spent one.
          el.call('resetWhitelabelTurnstile', { state: s })
          s.update({
            wraBusy: false,
            wraOk: !!(res && res.ok),
            wraSent: !!(res && res.ok),
            wraStatus: (res && res.message) || ''
          })
        }
      },

      FinePrint: {
        tag: 'div',
        fontSize: 'Y',
        color: (el, s) => schemeOf(s).inkSoft,
        hide: (el, s) => !(s.onboardingRoles && s.onboardingRoles.length),
        display: (el, s) => (s.onboardingRoles && s.onboardingRoles.length ? 'block' : 'none'),
        text: '{{ workspace.requestAccess.reviewNote | polyglot }}'
      }
    },

    // ── The sent state ──────────────────────────────────────────────────
    SentPane: {
      flow: 'y',
      gap: 'Y',
      hide: (el, s) => !s.wraSent,
      display: (el, s) => (s.wraSent ? 'flex' : 'none'),
      SentTitle: {
        tag: 'h3',
        margin: '0',
        fontSize: 'Z2',
        fontWeight: '700',
        text: '{{ workspace.requestAccess.sent | polyglot }}'
      },
      SentBody: {
        tag: 'p',
        margin: '0',
        fontSize: 'Z1',
        color: (el, s) => schemeOf(s).inkSoft,
        text: (el, s) => el.call('polyglot', s.wraStatus || 'workspace.requestAccess.sentBody')
      }
    }
  },

  // The one escape: this account, on this host, has nothing else to do.
  Session: {
    display: 'flex',
    align: 'center center',
    gap: 'V',
    margin: 'A 0 0',
    Who: {
      tag: 'span',
      fontSize: 'Y',
      color: (el, s) => schemeOf(s).inkSoft,
      text: (el, s) => {
        const u = _userOf(s.root)
        return u && u.email
          ? el.call('polyglot', 'workspace.requestAccess.signedInAs').replace('{email}', u.email)
          : ''
      }
    },
    SignOut: {
      tag: 'button',
      type: 'button',
      padding: '0',
      borderWidth: '0',
      cursor: 'pointer',
      fontSize: 'Y',
      fontWeight: '500',
      background: 'none',
      color: (el, s) => accentOf(s),
      text: '{{ workspace.requestAccess.signOut | polyglot }}',
      onClick: (e, el, s) => el.call('signoutAndRedirect', { sdk: el.sdk })
    }
  }
}
